import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {nullCheck} from 'utils/div';
import {useCopyToClipboard} from 'common/hooks/useCopyToClipboard';

import {DATE_FORMATS, getDateByCustomFormat} from 'utils/date';
import {Column, Row, UserInfo} from './styled';
import {Divider, IconButton, Layout, Text} from '@phonectas/unifon-ui-kit';
import NewCopyIcon from 'common/components/icons/NewCopyIcon';
import {Invoice} from 'common/types/store/threeSixtyTypes';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import VismaDistributionType from 'common/components/VismaDistributionType';
import VismaDocumentType from 'common/components/VismaDistributionType/VismaDocumentType';

interface Props {
	activeRow?: Invoice;
}
const InvoiceInfoList: React.FC<Props> = ({activeRow}) => {
	const {t} = useTranslation();
	const [copyToClipboard] = useCopyToClipboard();
	const {invoice} = useThreeSixtyNewStore((state) => ({
		invoice: state?.activeRowVisma?.invoice,
	}), shallow);
	const handleCopyNumber = useCallback(() => {
		copyToClipboard(activeRow?.invoice_number ?? '');
	}, [copyToClipboard, activeRow?.invoice_number]);
	const originalDueDate = invoice?.InvoiceDate;
	const dueDate = invoice?.DueDate;
	// const isWarning = typeof data?.note === 'string' && data.note.trim() !== '';

	const column1Fields = useMemo(() => [
		{label: t('Invoice number'), value: (
			<Layout gap="xs" fullWidth nowrap alignItems="center" style={{maxHeight: '20.8px'}}>
				<Text variant="body1">{nullCheck(invoice?.InvoiceNumber)}</Text>
				{
					invoice?.InvoiceNumber && (
						<IconButton onClick={handleCopyNumber} style={{height: '18px', width: '18px'}}>
							<NewCopyIcon />
						</IconButton>
					)
				}
			</Layout>
		)},
		{label: t('Company'), value: activeRow?.customer_name},
		{label: t('Billing grp'), value: activeRow?.customer_no},		
		{label: t('ORG'), value: activeRow?.customer_org_no},
		{label: t('Invoice date'), value: getDateByCustomFormat(activeRow?.invoice_date, DATE_FORMATS.DD_MM_YYYY)},
		{label: t('Original due date'), value: getDateByCustomFormat(originalDueDate, DATE_FORMATS.DD_MM_YYYY)},
		{label: t('Last due date'), value: getDateByCustomFormat(dueDate, DATE_FORMATS.DD_MM_YYYY)},
		{label: t('Invoice type'), value: activeRow?.transaction_text},
		{label: t('Distribution type'), value: <VismaDistributionType type={activeRow?.doc_distrib_code || ''} />},
	], [activeRow, t, handleCopyNumber, originalDueDate, dueDate, invoice?.InvoiceNumber]);

	const column2Fields = useMemo(() => [
		{label: t('KID'), value: activeRow?.payment_cid},
		{label: t('Client account'), value: activeRow?.client_no},
		{label: t('Invoice address'), value: activeRow?.einvoice_address},
		{label: t('Debt collection case number'), value: activeRow?.debt_coll_case_no},
		{label: t('Transferred to debt'), value: getDateByCustomFormat(activeRow?.transf_to_debt_coll_date, DATE_FORMATS.DD_MM_YYYY)},
		{label: t('Collection amount'), value: activeRow?.transf_to_debt_coll_curr_amount},
		{label: t('Collection code'), value: nullCheck(activeRow?.debt_coll_status_code)},
		{label: t('Collection status'), value: nullCheck(activeRow?.debt_coll_status_text)},
		{label: t('Document type'), value: <VismaDocumentType type={activeRow?.doc_type_code || ''} />},

	], [activeRow, t]);

	return (
		<UserInfo style={{width: '100%'}}>
			<Row style={{gap: '10px'}}>
				<Column>
					{column1Fields.map((field, index) => (
						<Layout gap="none" fullWidth nowrap key={index}>
							<Layout fullWidth nowrap style={{maxWidth: '160px'}} alignItems="center">
								<Text variant="body1" color="textSecondary">
									{field.label}
								</Text>
							</Layout>
							<Layout fullWidth nowrap alignItems="center">
								<span>{field.value}</span>
							</Layout>
						</Layout>
					))}
				</Column>
				<Divider vertical />
				<Column>
					{column2Fields.map((field, index) => (
						<Layout gap="none" fullWidth nowrap key={index}>
							<Layout fullWidth nowrap style={{maxWidth: '215px'}}>
								<Text variant="body1" color="textSecondary">
									{field.label}
								</Text>
							</Layout>
							<Layout fullWidth nowrap>
								<span>{field.value}</span>
							</Layout>
						</Layout>
					))}
				</Column>
			</Row>
		</UserInfo>
	);
}

export default React.memo(InvoiceInfoList);
